/**
 * gsap-text-animations.js
 * ========================================
 * GSAP Text Animation Library for Shopify
 * ========================================
 * 
 * Provides data-attribute-based text and element animations using
 * GSAP, ScrollTrigger, and SplitType.
 * 
 * DEPENDENCIES:
 * - GSAP + ScrollTrigger (loaded via gsap-animations.js)
 * - SplitType (loaded via theme.liquid)
 * - Listens for 'theme:gsap:ready' event before initializing
 * 
 * ========================================
 * ANIMATION DATA ATTRIBUTES
 * ========================================
 * 
 * TEXT ANIMATIONS (Page Load):
 * - data-anim="fade-up-word"           Words fade up from below (50px, duration 1s, stagger 0.08s)
 * - data-anim="fade-up-word-p"         Paragraph words fade up (30px, duration 0.8s, stagger 0.04s, delay 0.3s)
 * - data-anim="slide-up-char"          Characters slide up from 120% (duration 0.8s, stagger 0.02s, delay 0.5s)
 * - data-anim="slide-up-char-p"        Paragraph chars slide up from 100% (duration 0.6s, stagger 0.01s, delay 0.8s)
 * - data-anim="fade-up-line"           Lines fade up (50px, duration 0.8s, stagger 0.12s)
 * 
 * TEXT ANIMATIONS (Scroll Triggered):
 * - data-anim="scroll-fade-up-word"    Words fade up on scroll (50px, duration 1s, stagger 0.08s)
 * - data-anim="scroll-fade-up-word-p"  Paragraph words on scroll (30px, duration 0.8s, stagger 0.04s)
 * - data-anim="scroll-slide-up-char"   Characters slide up on scroll (120%, duration 0.8s, stagger 0.02s)
 * - data-anim="scroll-slide-up-char-p" Paragraph chars on scroll (100%, duration 0.6s, stagger 0.01s)
 * - data-anim="scroll-fade-up-line"    Lines fade up on scroll (50px, duration 0.8s, stagger 0.12s)
 * 
 * ELEMENT ANIMATIONS (Scroll Triggered):
 * - data-anim="slide-up"               Element slides up 60px (duration 0.8s)
 * - data-anim="fade-up"                Element fades up 30px (duration 0.8s)
 * - data-anim="scale-up"               Element scales from 0.8 to 1 (duration 0.8s)
 * - data-anim="slide-left"             Element slides from left -60px (duration 0.8s)
 * - data-anim="slide-right"            Element slides from right 60px (duration 0.8s)
 * 
 * ANIMATION CONTAINERS:
 * - data-anim-container="load"         Sequences animations on page load (0.4s stagger between children)
 * - data-anim-container="scroll"       Sequences animations when entering viewport (0.2s stagger)
 * 
 * ADVANCED ANIMATIONS:
 * - data-marquee-scroll                Infinite scroll marquee (reverses with scroll direction)
 *   - Optional: data-speed="1"         Marquee speed multiplier (default: 1)
 * 
 * - data-tilt-scale                    3D tilt and scale parallax effect
 *   - Optional: data-tilt-direction="rotateY|rotateX|both" (default: rotateY)
 *   - Optional: data-tilt-intensity="18" (default: 18)
 *   - Optional: data-scale-amount="1.2" (default: 1.2)
 *   - Optional: data-scrub="1.2" (default: 1.2 desktop, 0.5 touch)
 *   - Optional: data-tilt-scale-container (parent container for trigger)
 * 
 * - data-scale-scroll                  Scale and opacity animation for videos/images
 *   - Optional: data-start-scale="0.7" (default: 0.7)
 *   - Optional: data-end-scale="1" (default: 1)
 *   - Optional: data-start-opacity="0.6" (default: 0.6)
 *   - Optional: data-end-opacity="1" (default: 1)
 *   - Optional: data-scrub="1.5" (default: 1.5)
 * 
 * CUSTOM TIMING:
 * - data-anim-delay="500"              Add delay in milliseconds to any animation
 * 
 * ========================================
 * USAGE EXAMPLES
 * ========================================
 * 
 * Basic heading animation:
 * <h1 data-anim="fade-up-word">Welcome to our store</h1>
 * 
 * Paragraph text:
 * <p data-anim="fade-up-word-p">This text will fade in word by word</p>
 * 
 * Element animation with delay:
 * <div data-anim="fade-up" data-anim-delay="300">Delayed content</div>
 * 
 * Animation container (sequences children):
 * <div data-anim-container="load">
 *   <h2 data-anim="fade-up-word">First</h2>
 *   <p data-anim="fade-up-word-p">Second</p>
 *   <button data-anim="scale-up">Third</button>
 * </div>
 * 
 * Scroll-triggered marquee:
 * <div data-marquee-scroll data-speed="2">
 *   <span>Scroll Text • Scroll Text • Scroll Text • </span>
 * </div>
 * 
 * 3D parallax image:
 * <img src="product.jpg" data-tilt-scale data-tilt-direction="both" data-scale-amount="1.3">
 * 
 * Video scale on scroll:
 * <video data-scale-scroll data-start-scale="0.8" data-end-scale="1.1"></video>
 * 
 * ========================================
 * MANUAL REPLAY FUNCTIONS
 * ========================================
 * 
 * Call from browser console or custom JS:
 * - window.replayFadeUpWord()
 * - window.replaySlideUpChar()
 * - window.replayScrollFadeUpWord()
 * - window.replayScrollSlideUpChar()
 * - window.replayFadeUpLine()
 * - window.replayScrollFadeUpLine()
 */

(function() {
  'use strict';
  
  // Prevent double initialization if this script is loaded multiple times
  if (typeof window.GSAPTextAnimationsInitialized !== 'undefined') {
    return;
  }
  window.GSAPTextAnimationsInitialized = true;

  // ── Design-mode guard ───────────────────────────────────────
  // In the Shopify theme editor, Shopify.designMode is true.
  // We skip all animations there to avoid conflicts with the
  // editor's iframe scroll container.
  var isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode === true;
  
  if (isDesignMode) {
    console.log('[GSAP Text Animations] Design mode detected - skipping animations');
    return;
  }

  // Wait for GSAP to be ready (loaded by gsap-animations.js)
  document.addEventListener('theme:gsap:ready', function() {
    
    function waitForSplitType(callback) {
      if (typeof SplitType !== 'undefined') {
        callback();
      } else {
        setTimeout(() => waitForSplitType(callback), 100);
      }
    }
    
    waitForSplitType(function() {
      gsap.registerPlugin(ScrollTrigger);
      // NOTE: Do NOT call ScrollTrigger.normalizeScroll() here.
      // Lenis (initialised in gsap-animations.js) already normalises scroll
      // events and feeds them to ScrollTrigger via lenis.on('scroll', ...).
      // Calling normalizeScroll on top of Lenis causes two systems to fight
      // over pointer/touch events, producing visible scroll jitter.
      gsap.config({
        nullTargetWarn: false
      });
      
      let _scrollTweens = [];

      // Clean up inline styles on strong elements to prevent line breaks
      function cleanupStrongStyles() {
        document.querySelectorAll('[data-anim="scroll-fade-up-line"] .line strong, [data-anim="fade-up-line"] .line strong').forEach(strong => {
          strong.style.display = 'inline';
          strong.style.position = 'static';
        });
      }

      let fadeUpWordSplit = new SplitType('[data-anim="fade-up-word"]:not([data-anim-container] [data-anim="fade-up-word"]), [data-anim="fade-up-word-p"]:not([data-anim-container] [data-anim="fade-up-word-p"])', {
        types: 'lines, words'
      });
      let slideUpCharSplit = new SplitType('[data-anim="slide-up-char"]:not([data-anim-container] [data-anim="slide-up-char"]), [data-anim="slide-up-char-p"]:not([data-anim-container] [data-anim="slide-up-char-p"])', {
        types: 'lines, words, chars'
      });
      let scrollFadeUpWordSplit = new SplitType('[data-anim="scroll-fade-up-word"], [data-anim="scroll-fade-up-word-p"]', {
        types: 'lines, words'
      });
      let scrollSlideUpCharSplit = new SplitType('[data-anim="scroll-slide-up-char"], [data-anim="scroll-slide-up-char-p"]', {
        types: 'lines, words, chars'
      });
      let fadeUpLineSplit = new SplitType('[data-anim="fade-up-line"]', {
        types: 'lines',
        splitInlineTags: false
      });
      let scrollFadeUpLineSplit = new SplitType('[data-anim="scroll-fade-up-line"]', {
        types: 'lines',
        splitInlineTags: false
      });

      cleanupStrongStyles();

      // ========== TEXT ANIMATIONS - PAGE LOAD ==========
      
      document.querySelectorAll('[data-anim="fade-up-word"]').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;
        gsap.set(el.querySelectorAll('.word'), {
          y: 50,
          autoAlpha: 0,
          force3D: true,
          willChange: 'transform, opacity'
        });
        const tl = gsap.timeline({ delay: customDelay });
        tl.to(el.querySelectorAll('.word'), {
          y: 0,
          autoAlpha: 1,
          duration: 1,
          ease: 'cubic-bezier(.27, 1, .48, 1)',
          stagger: 0.08,
          force3D: true,
          clearProps: 'willChange'
        }, 0);
      });
      
      document.querySelectorAll('[data-anim="fade-up-word-p"]').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;
        const finalDelay = 0.3 + customDelay;
        gsap.set(el.querySelectorAll('.word'), {
          y: 30,
          autoAlpha: 0,
          force3D: true,
          willChange: 'transform, opacity'
        });
        const tl = gsap.timeline({ delay: finalDelay });
        tl.to(el.querySelectorAll('.word'), {
          y: 0,
          autoAlpha: 1,
          duration: 0.8,
          ease: 'cubic-bezier(.27, 1, .48, 1)',
          stagger: 0.04,
          force3D: true,
          clearProps: 'willChange'
        }, 0);
      });
      
      document.querySelectorAll('[data-anim="slide-up-char"]').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;
        const finalDelay = 0.5 + customDelay;
        gsap.set(el.querySelectorAll('.char'), {
          y: '120%',
          autoAlpha: 0,
          force3D: true,
          willChange: 'transform, opacity'
        });
        const tl = gsap.timeline({ delay: finalDelay });
        tl.to(el.querySelectorAll('.char'), {
          y: '0%',
          autoAlpha: 1,
          duration: 0.8,
          ease: 'cubic-bezier(.34, 1, .64, 1)',
          stagger: 0.02,
          force3D: true,
          clearProps: 'willChange'
        }, 0);
      });
      
      document.querySelectorAll('[data-anim="slide-up-char-p"]').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;
        const finalDelay = 0.8 + customDelay;
        gsap.set(el.querySelectorAll('.char'), {
          y: '100%',
          autoAlpha: 0,
          force3D: true,
          willChange: 'transform, opacity'
        });
        const tl = gsap.timeline({ delay: finalDelay });
        tl.to(el.querySelectorAll('.char'), {
          y: '0%',
          autoAlpha: 1,
          duration: 0.6,
          ease: 'cubic-bezier(.34, 1, .64, 1)',
          stagger: 0.01,
          force3D: true,
          clearProps: 'willChange'
        }, 0);
      });
      
      document.querySelectorAll('[data-anim="fade-up-line"]').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;
        const separators = el.querySelectorAll('.hero-parallax__separator');
        gsap.set(el.querySelectorAll('.line'), {
          y: 50,
          autoAlpha: 0,
          force3D: true,
          willChange: 'transform, opacity'
        });
        if (separators.length) {
          gsap.set(separators, {
            y: 8,
            autoAlpha: 0,
            force3D: true,
            willChange: 'transform, opacity'
          });
        }
        const tl = gsap.timeline({ delay: customDelay });
        tl.to(el.querySelectorAll('.line'), {
          y: 0,
          autoAlpha: 1,
          duration: 0.8,
          ease: 'cubic-bezier(.27, 1, .48, 1)',
          stagger: 0.12,
          force3D: true,
          clearProps: 'willChange'
        }, 0);
        if (separators.length) {
          tl.to(separators, {
            y: 0,
            autoAlpha: 1,
            duration: 0.35,
            ease: 'power2.out',
            force3D: true,
            clearProps: 'all'
          }, 0.25);
        }
      });

      // Header wrapper: slide down on initial page load.
      document.querySelectorAll('[data-anim="header-load-slide"]').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;

        // Wrapper slides in as a unit
        gsap.fromTo(el, {
          yPercent: -100,
          autoAlpha: 0,
          force3D: true,
          willChange: 'transform, opacity'
        }, {
          yPercent: 0,
          autoAlpha: 1,
          duration: 0.9,
          delay: customDelay + 0.2,
          ease: 'cubic-bezier(.22, 1, .36, 1)',
          force3D: true,
          clearProps: 'transform,opacity,willChange'
        });

        // Respect user motion preference for child stagger
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        // Child elements stagger in after the wrapper begins sliding down.
        // Each group: y -15px, autoAlpha 0 → settled position, autoAlpha 1.
        // Delays are relative to the wrapper animation start.
        const headerChildGroups = [
          // Logo + mobile drawer toggle appear together first
          { selector: '.header__heading-link, header-drawer',   delay: customDelay + 0.30 },
          // Nav menu
          { selector: '.header__inline-menu',                   delay: customDelay + 0.45 },
          // Header utilities except the absolutely-positioned cart icon and CTA
          { selector: '.header__icons > :not(.header__cta-btn):not(#cart-icon-bubble)', delay: customDelay + 0.55 },
          // CTA button(s)
          { selector: '.header__cta-btn',                       delay: customDelay + 0.65 },
        ];

        headerChildGroups.forEach(({ selector, delay }) => {
          const targets = el.querySelectorAll(selector);
          if (!targets.length) return;
          gsap.fromTo(targets, {
            y: -15,
            autoAlpha: 0,
            force3D: true,
            willChange: 'transform, opacity'
          }, {
            y: 0,
            autoAlpha: 1,
            duration: 0.6,
            delay,
            ease: 'cubic-bezier(.27, 1, .48, 1)',
            force3D: true,
            clearProps: 'transform,opacity,willChange'
          });
        });
      });
      
      // ========== TEXT ANIMATIONS - SCROLL TRIGGERED ==========
      
      document.querySelectorAll('[data-anim="scroll-fade-up-word"]').forEach(el => {
        const _t = gsap.from(el.querySelectorAll('.word'), {
          y: 50,
          autoAlpha: 0,
          duration: 1,
          ease: 'cubic-bezier(.27, 1, .48, 1)',
          stagger: 0.08,
          force3D: true,
          onComplete: () => el.setAttribute('data-anim-done', 'true'),
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            end: 'top 20%',
            markers: false,
          },
          clearProps: 'willChange'
        });
        _scrollTweens.push(_t);
      });
      
      document.querySelectorAll('[data-anim="scroll-fade-up-word-p"]').forEach(el => {
        const _t = gsap.from(el.querySelectorAll('.word'), {
          y: 30,
          autoAlpha: 0,
          duration: 0.8,
          ease: 'cubic-bezier(.27, 1, .48, 1)',
          stagger: 0.04,
          delay: 0.3,
          force3D: true,
          onComplete: () => el.setAttribute('data-anim-done', 'true'),
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            end: 'top 20%',
            markers: false,
          },
          clearProps: 'willChange'
        });
        _scrollTweens.push(_t);
      });
      
      document.querySelectorAll('[data-anim="scroll-slide-up-char"]').forEach(el => {
        const _t = gsap.from(el.querySelectorAll('.char'), {
          y: '120%',
          autoAlpha: 0,
          duration: 0.8,
          ease: 'cubic-bezier(.34, 1, .64, 1)',
          stagger: 0.02,
          delay: 0.5,
          force3D: true,
          onComplete: () => el.setAttribute('data-anim-done', 'true'),
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            end: 'top 20%',
            markers: false,
          },
          clearProps: 'willChange'
        });
        _scrollTweens.push(_t);
      });
      
      document.querySelectorAll('[data-anim="scroll-slide-up-char-p"]').forEach(el => {
        const _t = gsap.from(el.querySelectorAll('.char'), {
          y: '100%',
          autoAlpha: 0,
          duration: 0.6,
          ease: 'cubic-bezier(.34, 1, .64, 1)',
          stagger: 0.01,
          delay: 0.8,
          force3D: true,
          onComplete: () => el.setAttribute('data-anim-done', 'true'),
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            end: 'top 20%',
            markers: false,
          },
          clearProps: 'willChange'
        });
        _scrollTweens.push(_t);
      });
      
      document.querySelectorAll('[data-anim="scroll-fade-up-line"]').forEach(el => {
        const _t = gsap.from(el.querySelectorAll('.line'), {
          y: 50,
          autoAlpha: 0,
          duration: 0.8,
          ease: 'cubic-bezier(.27, 1, .48, 1)',
          stagger: 0.12,
          force3D: true,
          onComplete: () => el.setAttribute('data-anim-done', 'true'),
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            end: 'top 20%',
            markers: false,
          },
          clearProps: 'willChange'
        });
        _scrollTweens.push(_t);
      });
      
      // ========== ELEMENT ANIMATIONS - SCROLL TRIGGERED ==========
      
      document.querySelectorAll('[data-anim="slide-up"]:not([data-anim-container] [data-anim="slide-up"])').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;
        gsap.set(el, { y: 60, autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
        gsap.to(el, {
          y: 0,
          autoAlpha: 1,
          duration: 0.8,
          ease: 'cubic-bezier(.27, 1, .48, 1)',
          delay: customDelay,
          force3D: true,
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            markers: false,
          },
          clearProps: 'willChange'
        });
      });
      
      document.querySelectorAll('[data-anim="fade-up"]:not([data-anim-container] [data-anim="fade-up"])').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;
        gsap.from(el, {
          y: 30,
          autoAlpha: 0,
          duration: 0.8,
          ease: 'cubic-bezier(.27, 1, .48, 1)',
          delay: customDelay,
          force3D: true,
          onStart: () => el.setAttribute('data-anim-active', 'true'),
          onComplete: () => {
            el.removeAttribute('data-anim-active');
            el.setAttribute('data-anim-done', 'true');
          },
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            markers: false,
          },
          clearProps: 'all'
        });
      });
      
      document.querySelectorAll('[data-anim="scale-up"]:not([data-anim-container] [data-anim="scale-up"])').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;
        gsap.from(el, {
          scale: 0.8,
          autoAlpha: 0,
          duration: 0.8,
          ease: 'cubic-bezier(.34, 1, .64, 1)',
          delay: customDelay,
          force3D: true,
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            markers: false,
          },
          clearProps: 'all'
        });
      });
      
      document.querySelectorAll('[data-anim="slide-left"]:not([data-anim-container] [data-anim="slide-left"])').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;
        gsap.from(el, {
          x: -60,
          autoAlpha: 0,
          duration: 0.8,
          ease: 'cubic-bezier(.27, 1, .48, 1)',
          delay: customDelay,
          force3D: true,
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            markers: false,
          },
          clearProps: 'all'
        });
      });
      
      document.querySelectorAll('[data-anim="slide-right"]:not([data-anim-container] [data-anim="slide-right"])').forEach(el => {
        const customDelay = el.getAttribute('data-anim-delay') ? parseFloat(el.getAttribute('data-anim-delay')) / 1000 : 0;
        gsap.from(el, {
          x: 60,
          autoAlpha: 0,
          duration: 0.8,
          ease: 'cubic-bezier(.27, 1, .48, 1)',
          delay: customDelay,
          force3D: true,
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            markers: false,
          },
          clearProps: 'all'
        });
      });
      
      // ========== ANIMATION CONTAINERS ==========
      
      // Load containers - animate children on page load
      const loadContainers = document.querySelectorAll('[data-anim-container="load"]');
      loadContainers.forEach(container => {
        const animatedElements = container.querySelectorAll('[data-anim]');
        if (animatedElements.length === 0) return;
        
        animatedElements.forEach(el => {
          const animType = el.getAttribute('data-anim');
          if (animType.includes('fade-up-word')) {
            new SplitType(el, { types: 'lines, words' });
          } else if (animType.includes('slide-up-char')) {
            new SplitType(el, { types: 'lines, words, chars' });
          } else if (animType.includes('fade-up-line')) {
            new SplitType(el, { types: 'lines' });
          }
        });
        
        const tl = gsap.timeline();
        animatedElements.forEach((el, index) => {
          const animType = el.getAttribute('data-anim');
          const customDelay = el.getAttribute('data-anim-delay');
          const elementDelay = index * 0.4 + (customDelay ? parseFloat(customDelay) / 1000 : 0);
          
          if (animType === 'fade-up-word') {
            gsap.set(el.querySelectorAll('.word'), { y: 50, autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            tl.to(el.querySelectorAll('.word'), { y: 0, autoAlpha: 1, duration: 1, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.08, force3D: true, clearProps: 'willChange' }, elementDelay);
          } else if (animType === 'fade-up-word-p') {
            gsap.set(el.querySelectorAll('.word'), { y: 30, autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            tl.to(el.querySelectorAll('.word'), { y: 0, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.04, force3D: true, clearProps: 'willChange' }, elementDelay);
          } else if (animType === 'slide-up-char') {
            gsap.set(el.querySelectorAll('.char'), { y: '120%', autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            tl.to(el.querySelectorAll('.char'), { y: '0%', autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.34, 1, .64, 1)', stagger: 0.02, force3D: true, clearProps: 'willChange' }, elementDelay);
          } else if (animType === 'slide-up-char-p') {
            gsap.set(el.querySelectorAll('.char'), { y: '100%', autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            tl.to(el.querySelectorAll('.char'), { y: '0%', autoAlpha: 1, duration: 0.6, ease: 'cubic-bezier(.34, 1, .64, 1)', stagger: 0.01, force3D: true, clearProps: 'willChange' }, elementDelay);
          } else if (animType === 'fade-up-line') {
            const separators = el.querySelectorAll('.hero-parallax__separator');
            gsap.set(el.querySelectorAll('.line'), { y: 50, autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            if (separators.length) {
              gsap.set(separators, { y: 8, autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            }
            tl.to(el.querySelectorAll('.line'), { y: 0, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.12, force3D: true, clearProps: 'willChange' }, elementDelay);
            if (separators.length) {
              tl.to(separators, { y: 0, autoAlpha: 1, duration: 0.35, ease: 'power2.out', force3D: true, clearProps: 'all' }, elementDelay + 0.25);
            }
          } else if (animType === 'fade-up') {
            gsap.set(el, { y: 30, autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            tl.to(el, { y: 0, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', force3D: true, clearProps: 'willChange' }, elementDelay);
          } else if (animType === 'slide-up') {
            gsap.set(el, { y: 60, autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            tl.to(el, { y: 0, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', force3D: true, clearProps: 'willChange' }, elementDelay);
          } else if (animType === 'scale-up') {
            gsap.set(el, { scale: 0.8, autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            tl.to(el, { scale: 1, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.34, 1, .64, 1)', force3D: true, clearProps: 'willChange' }, elementDelay);
          } else if (animType === 'slide-left') {
            gsap.set(el, { x: -60, autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            tl.to(el, { x: 0, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', force3D: true, clearProps: 'willChange' }, elementDelay);
          } else if (animType === 'slide-right') {
            gsap.set(el, { x: 60, autoAlpha: 0, force3D: true, willChange: 'transform, opacity' });
            tl.to(el, { x: 0, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', force3D: true, clearProps: 'willChange' }, elementDelay);
          }
        });
      });
      
      // ========== RESIZE HANDLER FOR RESPONSIVE TEXT SPLITS ==========
      
      let _splitResizeTimer;
      let _lastSplitWidth = window.innerWidth;
      
      function _handleSplitResize() {
        const newWidth = window.innerWidth;
        if (newWidth === _lastSplitWidth) return;
        _lastSplitWidth = newWidth;
        
        // Kill all tracked scroll text tweens + their ScrollTriggers
        _scrollTweens.forEach(t => {
          if (t && t.scrollTrigger) t.scrollTrigger.kill();
          if (t) t.kill();
        });
        _scrollTweens = [];
        
        // Revert + re-split LOAD animations
        fadeUpWordSplit.revert();
        fadeUpWordSplit = new SplitType('[data-anim="fade-up-word"]:not([data-anim-container] [data-anim="fade-up-word"]), [data-anim="fade-up-word-p"]:not([data-anim-container] [data-anim="fade-up-word-p"])', { types: 'lines, words' });
        
        slideUpCharSplit.revert();
        slideUpCharSplit = new SplitType('[data-anim="slide-up-char"]:not([data-anim-container] [data-anim="slide-up-char"]), [data-anim="slide-up-char-p"]:not([data-anim-container] [data-anim="slide-up-char-p"])', { types: 'lines, words, chars' });
        
        fadeUpLineSplit.revert();
        fadeUpLineSplit = new SplitType('[data-anim="fade-up-line"]', { types: 'lines', splitInlineTags: false });
        
        // Revert + re-split SCROLL animations, recreate tweens only for not-yet-triggered elements
        scrollFadeUpWordSplit.revert();
        scrollFadeUpWordSplit = new SplitType('[data-anim="scroll-fade-up-word"], [data-anim="scroll-fade-up-word-p"]', { types: 'lines, words' });
        document.querySelectorAll('[data-anim="scroll-fade-up-word"]').forEach(el => {
          const _t = gsap.from(el.querySelectorAll('.word'), {
            y: 50, autoAlpha: 0, duration: 1, ease: 'cubic-bezier(.27, 1, .48, 1)',
            stagger: 0.08, force3D: true,
            onComplete: () => el.setAttribute('data-anim-done', 'true'),
            scrollTrigger: { trigger: el, start: 'top 80%', end: 'top 20%', markers: false },
            clearProps: 'willChange'
          });
          _scrollTweens.push(_t);
        });
        document.querySelectorAll('[data-anim="scroll-fade-up-word-p"]').forEach(el => {
          const _t = gsap.from(el.querySelectorAll('.word'), {
            y: 30, autoAlpha: 0, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)',
            stagger: 0.04, delay: 0.3, force3D: true,
            onComplete: () => el.setAttribute('data-anim-done', 'true'),
            scrollTrigger: { trigger: el, start: 'top 80%', end: 'top 20%', markers: false },
            clearProps: 'willChange'
          });
          _scrollTweens.push(_t);
        });
        
        scrollSlideUpCharSplit.revert();
        scrollSlideUpCharSplit = new SplitType('[data-anim="scroll-slide-up-char"], [data-anim="scroll-slide-up-char-p"]', { types: 'lines, words, chars' });
        document.querySelectorAll('[data-anim="scroll-slide-up-char"]').forEach(el => {
          const _t = gsap.from(el.querySelectorAll('.char'), {
            y: '120%', autoAlpha: 0, duration: 0.8, ease: 'cubic-bezier(.34, 1, .64, 1)',
            stagger: 0.02, delay: 0.5, force3D: true,
            onComplete: () => el.setAttribute('data-anim-done', 'true'),
            scrollTrigger: { trigger: el, start: 'top 80%', end: 'top 20%', markers: false },
            clearProps: 'willChange'
          });
          _scrollTweens.push(_t);
        });
        document.querySelectorAll('[data-anim="scroll-slide-up-char-p"]').forEach(el => {
          const _t = gsap.from(el.querySelectorAll('.char'), {
            y: '100%', autoAlpha: 0, duration: 0.6, ease: 'cubic-bezier(.34, 1, .64, 1)',
            stagger: 0.01, delay: 0.8, force3D: true,
            onComplete: () => el.setAttribute('data-anim-done', 'true'),
            scrollTrigger: { trigger: el, start: 'top 80%', end: 'top 20%', markers: false },
            clearProps: 'willChange'
          });
          _scrollTweens.push(_t);
        });
        
        scrollFadeUpLineSplit.revert();
        scrollFadeUpLineSplit = new SplitType('[data-anim="scroll-fade-up-line"]', { types: 'lines', splitInlineTags: false });
        document.querySelectorAll('[data-anim="scroll-fade-up-line"]').forEach(el => {
          const _t = gsap.from(el.querySelectorAll('.line'), {
            y: 50, autoAlpha: 0, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)',
            stagger: 0.12, force3D: true,
            onComplete: () => el.setAttribute('data-anim-done', 'true'),
            scrollTrigger: { trigger: el, start: 'top 80%', end: 'top 20%', markers: false },
            clearProps: 'willChange'
          });
          _scrollTweens.push(_t);
        });

        cleanupStrongStyles();

        ScrollTrigger.refresh();
      }
      
      window.addEventListener('resize', () => {
        clearTimeout(_splitResizeTimer);
        _splitResizeTimer = setTimeout(_handleSplitResize, 150);
      });
      
      // ========== MANUAL REPLAY FUNCTIONS ==========
      
      window.replayFadeUpWord = function() {
        gsap.fromTo('[data-anim="fade-up-word"] .word', { y: 50, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 1, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.08, force3D: true });
        gsap.fromTo('[data-anim="fade-up-word-p"] .word', { y: 30, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.04, delay: 0.3, force3D: true });
      };
      
      window.replaySlideUpChar = function() {
        gsap.fromTo('[data-anim="slide-up-char"] .char', { y: '120%', autoAlpha: 0 }, { y: '0%', autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.34, 1, .64, 1)', stagger: 0.02, force3D: true });
        gsap.fromTo('[data-anim="slide-up-char-p"] .char', { y: '100%', autoAlpha: 0 }, { y: '0%', autoAlpha: 1, duration: 0.6, ease: 'cubic-bezier(.34, 1, .64, 1)', stagger: 0.01, delay: 0.3, force3D: true });
      };
      
      window.replayScrollFadeUpWord = function() {
        gsap.fromTo('[data-anim="scroll-fade-up-word"] .word', { y: 50, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 1, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.08, force3D: true });
        gsap.fromTo('[data-anim="scroll-fade-up-word-p"] .word', { y: 30, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.04, delay: 0.3, force3D: true });
      };
      
      window.replayScrollSlideUpChar = function() {
        gsap.fromTo('[data-anim="scroll-slide-up-char"] .char', { y: '120%', autoAlpha: 0 }, { y: '0%', autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.34, 1, .64, 1)', stagger: 0.02, force3D: true });
        gsap.fromTo('[data-anim="scroll-slide-up-char-p"] .char', { y: '100%', autoAlpha: 0 }, { y: '0%', autoAlpha: 1, duration: 0.6, ease: 'cubic-bezier(.34, 1, .64, 1)', stagger: 0.01, delay: 0.3, force3D: true });
      };
      
      window.replayFadeUpLine = function() {
        gsap.fromTo('[data-anim="fade-up-line"] .line', { y: 50, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.12, force3D: true });
        gsap.fromTo('[data-anim="fade-up-line"] .hero-parallax__separator', { y: 8, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.35, delay: 0.25, ease: 'power2.out', force3D: true, clearProps: 'all' });
      };
      
      window.replayScrollFadeUpLine = function() {
        gsap.fromTo('[data-anim="scroll-fade-up-line"] .line', { y: 50, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.12, force3D: true });
        gsap.fromTo('[data-anim="scroll-fade-up-line"] .hero-parallax__separator', { y: 8, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.35, delay: 0.25, ease: 'power2.out', force3D: true, clearProps: 'all' });
      };
      
      // ========== SCROLL CONTAINER ANIMATIONS ==========
      
      const containers = document.querySelectorAll('[data-anim-container="scroll"]');
      containers.forEach(container => {
        gsap.set(container, { autoAlpha: 0 });
      });
      
      const scrollContainerObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const container = entry.target;
            const animatedElements = container.querySelectorAll('[data-anim]');
            if (animatedElements.length === 0) return;
            
            animatedElements.forEach(el => {
              const animType = el.getAttribute('data-anim');
              if (animType.includes('fade-up-word')) {
                new SplitType(el, { types: 'lines, words' });
              } else if (animType.includes('slide-up-char')) {
                new SplitType(el, { types: 'lines, words, chars' });
              } else if (animType.includes('fade-up-line')) {
                new SplitType(el, { types: 'lines' });
              }
            });
            
            const tl = gsap.timeline({
              onStart: () => {
                gsap.set(container, { autoAlpha: 1 });
              }
            });
            
            animatedElements.forEach((el, index) => {
              const animType = el.getAttribute('data-anim');
              const customDelay = el.getAttribute('data-anim-delay');
              const elementDelay = index * 0.2 + (customDelay ? parseFloat(customDelay) / 1000 : 0);
              
              if (animType.includes('fade-up-word')) {
                tl.from(el.querySelectorAll('.word'), { y: 50, autoAlpha: 0, duration: 1, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.08, force3D: true, clearProps: 'willChange' }, elementDelay);
              } else if (animType.includes('slide-up-char')) {
                tl.from(el.querySelectorAll('.char'), { y: '120%', autoAlpha: 0, duration: 0.8, ease: 'cubic-bezier(.34, 1, .64, 1)', stagger: 0.02, force3D: true, clearProps: 'willChange' }, elementDelay);
              } else if (animType.includes('fade-up-line')) {
                tl.from(el.querySelectorAll('.line'), { y: 50, autoAlpha: 0, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', stagger: 0.12, force3D: true, clearProps: 'willChange' }, elementDelay);
                if (el.querySelector('.hero-parallax__separator')) {
                  tl.from(el.querySelectorAll('.hero-parallax__separator'), { y: 8, autoAlpha: 0, duration: 0.35, ease: 'power2.out', force3D: true, clearProps: 'all' }, elementDelay + 0.25);
                }
              } else if (animType === 'slide-up') {
                tl.from(el, { y: 60, autoAlpha: 0, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', force3D: true, clearProps: 'all' }, elementDelay);
              } else if (animType === 'fade-up') {
                tl.from(el, {
                  y: 30,
                  autoAlpha: 0,
                  duration: 0.8,
                  ease: 'cubic-bezier(.27, 1, .48, 1)',
                  force3D: true,
                  onStart: () => el.setAttribute('data-anim-active', 'true'),
                  onComplete: () => {
                    el.removeAttribute('data-anim-active');
                    el.setAttribute('data-anim-done', 'true');
                  },
                  clearProps: 'all'
                }, elementDelay);
              } else if (animType === 'scale-up') {
                tl.from(el, { scale: 0.8, autoAlpha: 0, duration: 0.8, ease: 'cubic-bezier(.34, 1, .64, 1)', force3D: true, clearProps: 'all' }, elementDelay);
              } else if (animType === 'slide-left') {
                tl.from(el, { x: -60, autoAlpha: 0, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', force3D: true, clearProps: 'all' }, elementDelay);
              } else if (animType === 'slide-right') {
                tl.from(el, { x: 60, autoAlpha: 0, duration: 0.8, ease: 'cubic-bezier(.27, 1, .48, 1)', force3D: true, clearProps: 'all' }, elementDelay);
              }
            });
            
            scrollContainerObserver.unobserve(container);
          }
        });
      }, {
        threshold: 0.2,
        rootMargin: '0px 0px -20% 0px'
      });
      
      containers.forEach(container => {
        scrollContainerObserver.observe(container);
      });
      
      // ========== ADVANCED ANIMATIONS ==========
      
      // Scroll Direction Marquee
      function initScrollDirectionMarquee() {
        const marqueeElements = document.querySelectorAll('[data-marquee-scroll]');
        marqueeElements.forEach((element) => {
          const baseSpeed = parseFloat(element.getAttribute('data-speed')) || 1;
          let currentDirection = 1;
          const originalContent = element.innerHTML;
          element.innerHTML = originalContent + originalContent + originalContent + originalContent;
          
          gsap.delayedCall(0.1, () => {
            const contentWidth = element.scrollWidth / 4;
            let xPos = 0;
            const setX = gsap.quickSetter(element, 'x', 'px');
            const wrapX = gsap.utils.wrap(-contentWidth, 0);
            
            const tick = () => {
              xPos -= baseSpeed * currentDirection;
              xPos = wrapX(xPos);
              setX(xPos);
            };
            
            gsap.ticker.add(tick);
            let lastScrollY = window.pageYOffset;
            
            window.addEventListener('scroll', () => {
              const currentScrollY = window.pageYOffset;
              const scrollDirection = currentScrollY > lastScrollY ? 1 : -1;
              if (scrollDirection !== currentDirection) {
                currentDirection = scrollDirection;
              }
              lastScrollY = currentScrollY;
            }, { passive: true });
          });
        });
      }
      initScrollDirectionMarquee();

      // Mobile-only Scroll Direction Marquee (< 990px)
      function initMobileMarquee() {
        const MOBILE_BREAKPOINT = 990;
        const elements = document.querySelectorAll('[data-marquee-scroll-mobile]');
        if (!elements.length) return;

        const instances = [];

        elements.forEach((element) => {
          instances.push({
            element,
            originalHTML: element.innerHTML,
            active: false,
            tickFn: null,
            scrollFn: null,
          });
        });

        function activate(inst) {
          if (inst.active) return;
          inst.active = true;
          const el = inst.element;
          const baseSpeed = parseFloat(el.getAttribute('data-speed')) || 1;

          el.innerHTML = inst.originalHTML + inst.originalHTML + inst.originalHTML + inst.originalHTML;

          gsap.delayedCall(0.3, () => {
            const contentWidth = el.scrollWidth / 4;
            if (contentWidth <= 0) {
              inst.active = false;
              el.innerHTML = inst.originalHTML;
              return;
            }
            let xPos = 0;
            const setX = gsap.quickSetter(el, 'x', 'px');
            const wrapX = gsap.utils.wrap(-contentWidth, 0);

            inst.tickFn = () => {
              xPos -= baseSpeed;
              xPos = wrapX(xPos);
              setX(xPos);
            };

            gsap.ticker.add(inst.tickFn);
          });
        }

        function deactivate(inst) {
          if (!inst.active) return;
          inst.active = false;
          if (inst.tickFn) gsap.ticker.remove(inst.tickFn);
          inst.tickFn = null;
          inst.element.innerHTML = inst.originalHTML;
          gsap.set(inst.element, { x: 0 });
        }

        function check() {
          const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
          instances.forEach((inst) => {
            if (isMobile) {
              activate(inst);
            } else {
              deactivate(inst);
            }
          });
        }

        check();
        window.addEventListener('resize', (() => {
          let timer;
          return () => {
            clearTimeout(timer);
            timer = setTimeout(check, 150);
          };
        })());
      }
      initMobileMarquee();
      
      // Tilt Scale Parallax
      function initTiltScaleParallax() {
        document.querySelectorAll('[data-tilt-scale]').forEach((element) => {
          const container = element.closest('[data-tilt-scale-container]') || element.parentElement;
          const tiltDirection = element.getAttribute('data-tilt-direction') || 'rotateY';
          const tiltIntensity = parseFloat(element.getAttribute('data-tilt-intensity')) || 18;
          const scaleAmount = parseFloat(element.getAttribute('data-scale-amount')) || 1.2;
          const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
          const scrubValue = parseFloat(element.getAttribute('data-scrub')) || (isTouchDevice ? 0.5 : 1.2);
          
          const animProps = {
            scale: scaleAmount,
            ease: 'none'
          };
          
          if (tiltDirection === 'rotateX') {
            animProps.rotateX = tiltIntensity;
          } else if (tiltDirection === 'rotateY') {
            animProps.rotateY = tiltIntensity;
          } else if (tiltDirection === 'both') {
            animProps.rotateX = tiltIntensity * 0.6;
            animProps.rotateY = tiltIntensity * 0.8;
          }
          
          gsap.fromTo(element, {
            rotateX: tiltDirection === 'rotateX' || tiltDirection === 'both' ? -tiltIntensity * 0.5 : 0,
            rotateY: tiltDirection === 'rotateY' || tiltDirection === 'both' ? -tiltIntensity * 0.3 : 0,
            scale: 1,
            force3D: true
          }, {
            ...animProps,
            scrollTrigger: {
              trigger: container,
              start: 'top bottom',
              end: 'bottom top',
              scrub: scrubValue,
              invalidateOnRefresh: true,
            }
          });
        });
      }
      initTiltScaleParallax();
      
      // Video Scale Scroll
      function initVideoScaleScroll() {
        document.querySelectorAll('[data-scale-scroll]').forEach((element) => {
          const startScale = parseFloat(element.getAttribute('data-start-scale')) || 0.7;
          const endScale = parseFloat(element.getAttribute('data-end-scale')) || 1;
          const startOpacity = parseFloat(element.getAttribute('data-start-opacity')) || 0.6;
          const endOpacity = parseFloat(element.getAttribute('data-end-opacity')) || 1;
          const scrubValue = parseFloat(element.getAttribute('data-scrub')) || 1.5;
          
          gsap.fromTo(element, {
            scale: startScale,
            opacity: startOpacity,
            force3D: true
          }, {
            scale: endScale,
            opacity: endOpacity,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: element,
              start: 'top bottom-=100',
              end: 'center center',
              scrub: scrubValue,
              invalidateOnRefresh: true,
            }
          });
        });
      }
      initVideoScaleScroll();
      
    });
  });
})();
